export type CargoKind = 'standard' | 'odc'
export type QuoteState = 'needs_data' | 'pricing' | 'approval' | 'sent'

export interface QuoteDraft {
  id: string
  customer: string
  origin: string
  destination: string
  equipment: string
  cargoKind: CargoKind
  readyDate: string
  weightKg: number | null
  lengthCm: number | null
  widthCm: number | null
  heightCm: number | null
  centreOfGravityKnown: boolean
  carrier?: string
  serviceMode?: string
  incoterm?: string
  freightTerms?: string
  quoteValidityDays?: number
  originFreeDays?: number
  destinationFreeDays?: number
  demurragePerDay?: number
  detentionPerDay?: number
  buyAmount: number
  marginPct: number
  state: QuoteState
  createdAt: string
}

export interface QuoteTotals {
  buy: number
  sell: number
  profit: number
}

export const QUOTE_STATE_LABEL: Record<QuoteState, string> = {
  needs_data: 'Needs data',
  pricing: 'Pricing',
  approval: 'Approval',
  sent: 'Sent',
}

export function quoteTotals(buyAmount: number, marginPct: number): QuoteTotals {
  const buy = Number.isFinite(buyAmount) ? Math.max(0, buyAmount) : 0
  const margin = Number.isFinite(marginPct) ? Math.min(95, Math.max(0, marginPct)) : 0
  const sell = margin >= 95 ? buy : buy / (1 - margin / 100)
  return { buy, sell: Math.round(sell), profit: Math.round(sell - buy) }
}

export function quoteBlockers(
  draft: Pick<
    QuoteDraft,
    | 'customer'
    | 'origin'
    | 'destination'
    | 'cargoKind'
    | 'centreOfGravityKnown'
    | 'weightKg'
    | 'lengthCm'
    | 'widthCm'
    | 'heightCm'
  >
): string[] {
  const blockers: string[] = []
  if (!draft.customer.trim()) blockers.push('Customer is required')
  if (!draft.origin.trim() || !draft.destination.trim())
    blockers.push('Origin and destination are required')
  if (draft.cargoKind === 'odc') {
    if (!draft.weightKg || !draft.lengthCm || !draft.widthCm || !draft.heightCm)
      blockers.push('ODC dimensions and weight are required')
    if (!draft.centreOfGravityKnown)
      blockers.push('Centre of gravity is required for engineering review')
  }
  return blockers
}

export function nextQuoteState(draft: QuoteDraft): QuoteState {
  if (quoteBlockers(draft).length) return 'needs_data'
  if (draft.marginPct < 12) return 'approval'
  return 'pricing'
}

export function quoteId(now = new Date()): string {
  const day = now.toISOString().slice(2, 10).replace(/-/g, '')
  const suffix = Math.random().toString(36).slice(2, 5).toUpperCase()
  return `FR-${day}-${suffix}`
}
